import { COUNTRY_MAP } from '../countries';
import { applyIntlAutocompleteBusinessRules } from '../utils/intlAutocompleteBusinessRules';
import { SMARTY_INTL_AUTOCOMPLETE_KEY } from '../utils/smartyConfig';
import useSmartyLookupBase from './useSmartyLookupBase';

const MIN_QUERY_LENGTH = 3;
const CACHE_NAMESPACE = 'smarty-intl-autocomplete';
const CACHE_TTL_MS = 60 * 1000;
const RATE_LIMIT_NAMESPACE = 'smarty-intl-autocomplete';
const RATE_LIMIT_CONFIG = {
  limit: 15,
  windowMs: 60 * 1000,
  cooldownMs: 30 * 1000
};
const LOOKUP_URL = 'https://international-autocomplete.api.smarty.com/v2/lookup';

const buildCacheKey = (trimmedQuery, countryCode, selectedAddressId) => JSON.stringify({
  query: trimmedQuery,
  countryCode,
  selectedAddressId: selectedAddressId || null
});

const buildLookupUrl = (trimmedQuery, countryCode, selectedAddressId) => {
  const iso3 = COUNTRY_MAP[countryCode].iso3;

  if (selectedAddressId) {
    return `${LOOKUP_URL}/${selectedAddressId}?key=${SMARTY_INTL_AUTOCOMPLETE_KEY}&country=${iso3}&max_results=10`;
  }

  return `${LOOKUP_URL}?key=${SMARTY_INTL_AUTOCOMPLETE_KEY}&country=${iso3}&search=${encodeURIComponent(trimmedQuery)}&max_results=10`;
};

const transformSuggestions = (candidates = []) => candidates.map((result) => {
  if (result.street) {
    return {
      ...result,
      displayText: `${result.street}, ${result.locality}, ${result.administrative_area} ${result.postal_code}`,
      isDetailed: true
    };
  }

  let displayText = result.address_text;
  if (result.entries > 1) {
    displayText += ` (${result.entries})`;
  }

  return {
    ...result,
    displayText,
    isDetailed: false
  };
});

const useSmartyInternationalLookup = (
  query,
  countryCode = '',
  selectedAddressId = null,
  debounceMs = 300
) => {
  const trimmedQuery = query.trim();
  const isEnabled = Boolean(
    (((trimmedQuery && trimmedQuery.length >= MIN_QUERY_LENGTH) || selectedAddressId)) &&
    countryCode &&
    COUNTRY_MAP[countryCode]
  );
  const cacheKey = buildCacheKey(trimmedQuery, countryCode, selectedAddressId);
  const requestUrl = isEnabled ? buildLookupUrl(trimmedQuery, countryCode, selectedAddressId) : '';

  return useSmartyLookupBase({
    isEnabled,
    debounceMs,
    cacheNamespace: CACHE_NAMESPACE,
    cacheTtlMs: CACHE_TTL_MS,
    rateLimitNamespace: RATE_LIMIT_NAMESPACE,
    rateLimitConfig: RATE_LIMIT_CONFIG,
    cacheKey,
    requestUrl,
    transformResponse: (responseData) => (
      applyIntlAutocompleteBusinessRules(
        countryCode,
        transformSuggestions(responseData.candidates || [])
      )
    ),
    onError: (err) => {
      console.error('Smarty International lookup failed:', err);
    },
    effectDeps: [query, countryCode, selectedAddressId]
  });
};

export default useSmartyInternationalLookup;
